// Mirrors UtilityConfigurations.Presentation.Contracts.UtilityDetailsResponse.
// Enums are serialized as numbers by the backend, so the values must match
// Shared.Domain.Enums exactly.

export enum UtilityStatus {
	Active = 0,
	Inactive = 1,
}

export enum UtilityLanguage {
	English = 0,
	French = 1,
}

/** Shape of both CountryResponse and CurrencyResponse. */
export interface UtilityConfigurationLookup {
	id: string;
	code: string;
	name: string;
}

/** Mirrors FeatureResponse — one feature flag enabled for the utility. */
export interface UtilityFeature {
	id: string;
	name: string;
	description: string;
	flagsMithCode: string;
}

/**
 * Mirrors Shared.Domain.Enums.UserType — only the two values the seeded feature
 * groups use: the customer app ("Mobile") and the utility admin portal ("Admin").
 */
export enum FeatureUserType {
	Customer = 0,
	Utility = 4,
}

/**
 * Mirrors FeatureGroupResponse — the catalogue of everything that can be switched
 * on for a utility. Name and description arrive already translated.
 */
export interface UtilityFeatureGroup {
	id: string;
	name: string;
	description: string;
	userType: FeatureUserType;
	features: UtilityFeature[];
}

export interface UtilityConfiguration {
	id: string;
	utilityName: string;
	subDomain: string;
	status: UtilityStatus;
	logo: string | null;
	acronym: string;
	currency: UtilityConfigurationLookup;
	country: UtilityConfigurationLookup;
	language: UtilityLanguage;
	primaryColor: string;
	secondaryColor: string;
	accentColor: string;
	createdAt: string;
	updatedAt: string;
}

/**
 * Mirrors UtilityWithFeaturesResponse — what GET by id returns, nested so that the
 * list endpoint's rows stay plain UtilityConfiguration. Both lists are empty until
 * the backend's Flagsmith integration lands. Note the names: the response carries
 * whole features, the create/edit bodies take bare ids under
 * customerFeatureFlags / adminFeatureFlags.
 */
export interface UtilityConfigurationDetails {
	utility: UtilityConfiguration;
	customerFeatures: UtilityFeature[];
	adminFeatures: UtilityFeature[];
}

export interface GetUtilityConfigurationsParams {
	page: number;
	pageSize: number;
	search?: string;
	isActive?: boolean;
}

/** ISO 639-1 codes — the create/edit endpoints take the code, not the enum name. */
export const UTILITY_LANGUAGES = [
	{ value: "en", label: "English" },
	{ value: "fr", label: "French" },
];

/** The reverse: responses carry the enum, the form carries the code. */
export const UTILITY_LANGUAGE_CODES: Record<UtilityLanguage, string> = {
	[UtilityLanguage.English]: "en",
	[UtilityLanguage.French]: "fr",
};

/** Multipart body of POST /utilityconfigurations/utility-configurations. */
export interface CreateUtilityConfigurationPayload {
	name: string;
	acronym: string;
	subDomain: string;
	currencyId: string;
	countryId: string;
	language: string;
	primaryColor: string;
	secondaryColor: string;
	accentColor: string;
	logo: File;
	/** Flagsmith codes of the features switched on, sent as a repeated form key. */
	customerFeatureFlags: string[];
	adminFeatureFlags: string[];
}

/** Multipart body of PUT /utilityconfigurations/utility-configurations/{id}. */
export interface EditUtilityConfigurationPayload extends Omit<CreateUtilityConfigurationPayload, "logo"> {
	id: string;
	/** Left out when the logo was not replaced — the backend then keeps the existing one. */
	logo: File | null;
}
